import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Page } from '../../components/shell/Page';
import SplitLayout, { PaneFooter } from '../../components/shell/SidePane';
import ExplorerLayout from '../../components/shell/ExplorerTree';
import useBankTree, { EXAM_TYPE_LABEL } from '../../hooks/useBankTree';
import { TreeStructure, Exam } from '@phosphor-icons/react';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { stripHtml } from '../../components/richtext/RichText';
import { useCategoryManagerPane } from '../../components/categories/CategoryManager';
import QuestionPreview from '../../components/questions/QuestionPreview';
import {
  Plus,
  Pencil,
  Trash,
  Upload,
  X,
  Question,
  FolderOpen,
  DownloadSimple,
  FileCsv,
  FileDoc,
  FileCode,
  CheckCircle,
  WarningCircle,
  Image,
  Eye,
  EyeSlash,
  CaretLeft,
  CaretRight
} from '@phosphor-icons/react';
import { QUESTION_TYPES, DIFFICULTIES } from './QuestionForm';


export default function QuestionBank() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  
  // Filters (all sent to the API as query params)
  const [filters, setFilters] = useState({
    question_type: '',
    difficulty: '',
    course_id: '',
    category_id: '',
  });
  // Explorer — only questions and quizzes / exams: Category (nested) → quiz / exam. Scopes the grid.
  const catalog = useBankTree({ api });
  const [treeKey, setTreeKey] = useState(null);
  const treeScope = useMemo(() => {
    if (!treeKey) return {};
    if (treeKey === 'unlinked') return { unlinked: true };
    const e = catalog.byKey(treeKey);
    if (!e) return {};
    if (e.kind === 'category') return e.node.data.category_id ? { category_id: e.node.data.category_id } : { course_ids: e.descendants.filter((d) => d.kind === 'course').map((d) => d.data.id) };
    if (e.kind === 'course') return { course_id: e.node.data.id };
    // a quiz inside the course: show the questions it uses
    if (e.kind === 'quiz-node') return e.node.data.exam_id ? { exam_id: e.node.data.exam_id } : { course_id: e.course?.data.id, node_ids: [e.node.data.id] };
    if (e.kind === 'node') return { course_id: e.course?.data.id, node_ids: [e.node.data.id, ...(e.node.descendantIds || [])], exam_ids_under: e.descendants.filter((d) => d.kind === 'quiz-node' && d.data.exam_id).map((d) => d.data.exam_id) };
    if (e.kind === 'exam') return { exam_id: e.node.data.id };
    if (e.kind === 'unfiled-exams') return { exam_ids: e.node.children.map((c) => c.data.id) };
    return {};
  }, [treeKey, catalog.byKey]);

  // Side pane states (small forms open in the resizable pane; the question editor is its own tab)
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [uploadTarget, setUploadTarget] = useState({ course_id: '', category_id: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  // Preview pane: the selected question(s) as a learner sees them, with the answer key
  const [previewList, setPreviewList] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewAnswers, setPreviewAnswers] = useState(true);
  // Categories are shared with Courses (one list): the manager pane + list come from useCategoryManagerPane
  const categoryMgr = useCategoryManagerPane({ api, onChange: () => { fetchQuestions(); catalog.reload(); } });
  const categories = categoryMgr.categories;

  const fetchQuestions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.question_type) params.append('question_type', filters.question_type);
      if (filters.difficulty) params.append('difficulty', filters.difficulty);
      // a quiz may reuse questions filed under another course, so don't narrow by course then
      if (treeScope.course_id && !treeScope.exam_ids_under?.length) params.append('course_id', treeScope.course_id);
      else if (treeScope.course_ids) params.append('course_ids', treeScope.course_ids.join(',') || '__none__');
      else if (filters.course_id) params.append('course_id', filters.course_id);
      if (treeScope.node_ids) params.append('node_ids', treeScope.node_ids.join(','));
      if (treeScope.exam_ids_under) params.append('exam_ids', treeScope.exam_ids_under.join(','));
      if (treeScope.category_id) params.append('category_id', treeScope.category_id);
      else if (filters.category_id) params.append('category_id', filters.category_id);
      if (treeScope.exam_id) params.append('exam_id', treeScope.exam_id);
      params.append('limit', '2000');
      
      if (treeScope.exam_ids) {
        // the "quizzes without a category" group: union of each exam's questions
        const lists = await Promise.all(treeScope.exam_ids.map((id) => api.get(`/question-bank?${params.toString()}&exam_id=${id}`).then((r) => r.data).catch(() => [])));
        const seen = new Set();
        setQuestions(lists.flat().filter((q) => (seen.has(q.id) ? false : seen.add(q.id))));
        return;
      }
      const res = await api.get(`/question-bank?${params.toString()}`);
      setQuestions(treeScope.unlinked ? res.data.filter((q) => !q.course_id) : res.data);
    } catch (error) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [api, filters, treeScope]);

  const fetchCourses = async () => {
    try {
      const res = await api.get('/courses');
      setCourses(res.data);
    } catch (error) {
      console.error('Failed to load courses');
    }
  };

  const fetchCategories = () => categoryMgr.reload();

  useEffect(() => {
    fetchQuestions();
    fetchCourses();
    fetchCategories();
  }, [fetchQuestions]);

  // The question editor is a big form → its own tab (QuestionForm.js)
  const handleCreateQuestion = () => {
    // New questions default to the course currently being viewed (if any); editable in the form.
    const qs = new URLSearchParams();
    const cid = treeScope.course_id || filters.course_id;
    if (cid) qs.set('course_id', cid);
    if (treeEntry?.kind === 'node') qs.set('node_id', treeEntry.node.data.id);
    const catId = treeScope.category_id || treeEntry?.category?.data?.category_id || filters.category_id;
    if (catId) qs.set('category_id', catId);
    navigate(`/instructor/questions/new${qs.toString() ? `?${qs}` : ''}`);
  };

  const handleEditQuestion = (question) => {
    navigate(`/instructor/questions/${question.id}/edit`);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      await api.delete(`/question-bank/${questionId}`);
      toast.success('Question deleted');
      fetchQuestions();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  // Bulk upload: .csv / .json / .docx → POST /question-bank/bulk-upload-file (multipart)
  const handleBulkUpload = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    if (uploadTarget.course_id) formData.append('course_id', uploadTarget.course_id);
    if (uploadTarget.category_id) formData.append('category_id', uploadTarget.category_id);
    setUploading(true);
    setUploadResult(null);
    try {
      const res = await api.post('/question-bank/bulk-upload-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      if (res.data.created > 0) toast.success(`Imported ${res.data.created} question${res.data.created === 1 ? '' : 's'}`);
      if (res.data.failed > 0) toast.warning(`${res.data.failed} question${res.data.failed === 1 ? '' : 's'} skipped — see the list in the pane`);
      fetchQuestions();
      fetchCategories();
      catalog.reload();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Template download (authenticated → fetch as blob, then save)
  const downloadTemplate = async (fmt) => {
    try {
      const res = await api.get(`/question-bank/templates/${fmt}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `question_import_template.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error('Could not download the template');
    }
  };

  // Category CRUD
  const getQuestionTypeIcon = (type) => {
    const found = QUESTION_TYPES.find(t => t.value === type);
    return found ? found.icon : Question;
  };

  const getCategoryById = (id) => categories.find(c => c.id === id);
  const getCourseTitle = (id) => courses.find(c => c.id === id)?.title || '';
  const getTypeLabel = (type) => QUESTION_TYPES.find(t => t.value === type)?.label || type || '';
  const DIFFICULTY_TONE = { easy: 'success', medium: 'warning', hard: 'danger' };
  const truncate = (text, n = 90) => {
    const s = text || '';
    return s.length > n ? `${s.slice(0, n).trimEnd()}…` : s;
  };
  const activeFilterCount = ['course_id', 'category_id', 'difficulty', 'question_type'].filter(k => filters[k]).length;

  // ─── Side pane (small CRUD forms open here instead of popups) ───
  const closePane = () => { setShowBulkUploadModal(false); categoryMgr.close(); setUploadResult(null); setUploadFile(null); setPreviewList([]); };
  const openPreview = (rows) => { closePane(); setPreviewList(rows); setPreviewIndex(0); };
  const openPane = (which) => { closePane(); which(true); };
  let side = null;
  if (previewList.length > 0) {
    const q = previewList[Math.min(previewIndex, previewList.length - 1)];
    const many = previewList.length > 1;
    side = {
      open: true, onOpenChange: closePane, title: 'Preview', subtitle: many ? `${previewIndex + 1} of ${previewList.length} selected` : stripHtml(q.question_text).slice(0, 60),
      icon: Eye, width: 46, testid: 'question-preview-pane', storageKey: 'pane-question-preview',
      actions: (
        <div className="flex items-center gap-1 mr-1">
          <button type="button" onClick={() => setPreviewAnswers((v) => !v)} className="h-7 px-2 rounded text-xs flex items-center gap-1 text-muted-foreground hover:bg-muted hover:text-foreground" title={previewAnswers ? 'Hide answer key (learner view)' : 'Show answer key'} data-testid="preview-toggle-answers">
            {previewAnswers ? <EyeSlash className="w-4 h-4" /> : <Eye className="w-4 h-4" />}{previewAnswers ? 'Hide answers' : 'Show answers'}
          </button>
          {many && (
            <>
              <button type="button" disabled={previewIndex === 0} onClick={() => setPreviewIndex((i) => i - 1)} className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40" title="Previous" data-testid="preview-prev"><CaretLeft className="w-4 h-4" /></button>
              <button type="button" disabled={previewIndex >= previewList.length - 1} onClick={() => setPreviewIndex((i) => i + 1)} className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:bg-muted disabled:opacity-40" title="Next" data-testid="preview-next"><CaretRight className="w-4 h-4" /></button>
            </>
          )}
        </div>
      ),
      children: (
        <QuestionPreview
          question={q}
          showAnswers={previewAnswers}
          index={many ? previewIndex : undefined}
          total={many ? previewList.length : undefined}
          courseName={courses.find((c) => c.id === q.course_id)?.title}
          categoryName={categories.find((c) => c.id === q.category_id)?.name}
        />
      ),
      footer: <PaneFooter onCancel={closePane} cancelLabel="Close" onSubmit={() => handleEditQuestion(q)} submitLabel="Edit question" submitIcon={Pencil} />,
    };
  } else if (categoryMgr.side) {
    side = categoryMgr.side;
  } else if (showBulkUploadModal) {
    const TEMPLATES = [
      { fmt: 'csv', label: 'CSV template', icon: FileCsv, hint: 'Spreadsheet: one row per question' },
      { fmt: 'json', label: 'JSON template', icon: FileCode, hint: 'For exports from other tools' },
      { fmt: 'docx', label: 'Word template', icon: FileDoc, hint: 'One table per question; figures allowed' },
    ];
    side = {
      open: true, onOpenChange: closePane, title: 'Bulk upload questions', subtitle: 'CSV, JSON or Word (.docx)', icon: Upload, width: 44, testid: 'bulk-upload-pane',
      children: (
        <div className="space-y-4">
          <div className="pane-grid">
            <div className="field">
              <Label>Add to course</Label>
              <select className="form-select" value={uploadTarget.course_id} onChange={(e) => setUploadTarget({ ...uploadTarget, course_id: e.target.value })} data-testid="upload-course-select">
                <option value="">General (no course)</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
            </div>
            <div className="field">
              <Label>Default category</Label>
              <select className="form-select" value={uploadTarget.category_id} onChange={(e) => setUploadTarget({ ...uploadTarget, category_id: e.target.value })} data-testid="upload-category-select">
                <option value="">None (use file's Category)</option>
                {categories.map((c) => <option key={c.id} value={c.id} title={c.path}>{'\u00a0\u00a0\u00a0'.repeat(c.depth || 0)}{c.depth ? '└ ' : ''}{c.name}</option>)}
              </select>
            </div>
          </div>

          <div className="field">
            <Label>File</Label>
            <label
              className={`block border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors ${uploading ? 'opacity-60' : 'hover:border-shell-primary hover:bg-muted/30'}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) { setUploadFile(f); handleBulkUpload(f); } }}
            >
              <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-1.5" />
              <p className="text-[13px] font-medium">{uploading ? 'Importing…' : uploadFile ? uploadFile.name : 'Drop a file here or click to choose'}</p>
              <p className="field-hint mt-0.5">.csv · .json · .docx — up to 25 MB</p>
              <input
                type="file"
                accept=".csv,.json,.docx,text/csv,application/json,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) { setUploadFile(f); handleBulkUpload(f); } e.target.value = ''; }}
                data-testid="bulk-upload-input"
              />
            </label>
          </div>

          {uploadResult && (
            <div className="space-y-2" data-testid="bulk-upload-result">
              <div className={`flex items-start gap-2 rounded border p-3 text-[13px] ${uploadResult.failed ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-emerald-200 bg-emerald-50 text-emerald-900'}`}>
                {uploadResult.failed ? <WarningCircle className="w-4 h-4 mt-0.5 flex-shrink-0" weight="fill" /> : <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" weight="fill" />}
                <div>
                  <div className="font-medium">{uploadResult.created} of {uploadResult.total} questions imported{uploadResult.filename ? ` from ${uploadResult.filename}` : ''}</div>
                  {uploadResult.failed > 0 && <div className="text-xs opacity-80">{uploadResult.failed} skipped — fix the rows below and upload again (only the skipped ones).</div>}
                </div>
              </div>
              {uploadResult.errors?.length > 0 && (
                <ul className="max-h-48 overflow-y-auto rounded border border-border divide-y text-xs">
                  {uploadResult.errors.map((e, i) => (
                    <li key={i} className="px-2.5 py-1.5 flex gap-2">
                      <span className="font-mono text-muted-foreground w-10 flex-shrink-0">#{e.row}</span>
                      <span className="min-w-0"><span className="text-destructive">{e.error}</span>{e.question ? <span className="text-muted-foreground"> — {e.question}</span> : null}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Templates</Label>
            <div className="grid grid-cols-1 gap-1.5">
              {TEMPLATES.map((t) => {
                const Icon = t.icon;
                return (
                  <button key={t.fmt} type="button" onClick={() => downloadTemplate(t.fmt)} className="flex items-center gap-2.5 rounded border border-border bg-white px-2.5 py-2 text-left hover:border-shell-primary hover:bg-muted/30" data-testid={`template-${t.fmt}`}>
                    <Icon className="w-5 h-5 text-shell-primary flex-shrink-0" weight="duotone" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-medium">{t.label}</span>
                      <span className="block field-hint">{t.hint}</span>
                    </span>
                    <DownloadSimple className="w-4 h-4 text-muted-foreground" />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="field-hint space-y-1">
            <p><strong>Word layout:</strong> one table per question — rows <code>Question</code> (text + optional picture), <code>Type</code>, <code>Option</code> (text · correct/incorrect), <code>Solution</code>, <code>Marks</code> (marks · negative). Optional rows: <code>Answer</code>, <code>Hint</code>, <code>Topic</code> (topic · difficulty), <code>Category</code>.</p>
            <p><strong>Types:</strong> multiple_choice, multiple_select, true_false, fill_blank, short_answer, numeric, essay. A category named in the file is created if it doesn't exist.</p>
          </div>
        </div>
      ),
      footer: <PaneFooter onCancel={closePane} cancelLabel="Close" />,
    };
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse">Loading questions...</div>
        </div>
      </DashboardLayout>
    );
  }

  const treeEntry = treeKey ? catalog.byKey(treeKey) : null;
  const explorer = {
    title: 'Question bank', icon: TreeStructure, nodes: catalog.nodes, selectedKey: treeKey, loading: catalog.loading, storageKey: 'questions',
    onSelect: (n) => setTreeKey(n ? n.key : null), emptyText: 'No categories yet — use Categories to add the first one',
    menuFor: (n) => (n.kind === 'exam' ? [
      { key: 'open', label: 'Open quiz / exam', icon: Exam, onClick: () => navigate(`/instructor/exams/${n.data.id}`) },
      { key: 'preview', label: 'Preview', icon: Eye, onClick: () => navigate(`/instructor/exams/${n.data.id}/preview`) },
    ] : n.kind === 'category' ? [
      { key: 'new-q', label: 'New question here', icon: Plus, onClick: () => navigate(`/instructor/questions/new${n.data.category_id ? `?category_id=${n.data.category_id}` : ''}`) },
      { key: 'manage', label: 'Manage categories', icon: FolderOpen, onClick: () => categoryMgr.open() },
    ] : n.kind === 'course' ? [
      { key: 'new-q', label: 'New question here', icon: Plus, onClick: () => navigate(`/instructor/questions/new?course_id=${n.data.id}`) },
      { key: 'structure', label: 'Open course structure', icon: TreeStructure, onClick: () => navigate(`/instructor/courses/${n.data.id}/structure`) },
    ] : n.kind === 'quiz-node' ? [
      { key: 'open', label: 'Open quiz / exam', icon: Exam, onClick: () => navigate(`/instructor/exams/${n.data.exam_id}`), disabled: !n.data.exam_id },
      { key: 'preview', label: 'Preview', icon: Eye, onClick: () => navigate(`/instructor/exams/${n.data.exam_id}/preview`), disabled: !n.data.exam_id },
      { key: 'edit-node', label: 'Edit in course', icon: TreeStructure, onClick: () => { const e = catalog.byKey(n.key); navigate(`/instructor/courses/${e?.course?.data.id}/nodes/${n.data.id}`); } },
    ] : n.kind === 'node' ? [
      { key: 'new-q', label: 'New question here', icon: Plus, onClick: () => { const e = catalog.byKey(n.key); navigate(`/instructor/questions/new?course_id=${e?.course?.data.id || ''}&node_id=${n.data.id}`); } },
      { key: 'structure', label: 'Open in course structure', icon: TreeStructure, onClick: () => { const e = catalog.byKey(n.key); navigate(`/instructor/courses/${e?.course?.data.id}/structure?node=${n.data.id}`); } },
    ] : null),
    footer: catalog.data ? `${catalog.data.question_count || 0} questions · ${catalog.data.exam_count || 0} quizzes & exams in the bank` : null,
  };
  const scopeLabel = treeEntry ? (treeEntry.kind === 'category' ? `Category: ${treeEntry.node.data.path || treeEntry.node.label}` : treeEntry.kind === 'course' ? `Course: ${treeEntry.node.label}` : (treeEntry.kind === 'node' || treeEntry.kind === 'quiz-node') ? `${treeEntry.node.title?.split(':')[0] || 'Node'}: ${treeEntry.node.label}` : treeEntry.kind === 'exam' ? `${EXAM_TYPE_LABEL[treeEntry.node.data.assessment_type] || 'Exam'}: ${treeEntry.node.label}` : treeEntry.kind === 'unfiled-exams' ? 'Quizzes & exams without a place' : 'Questions not linked to a course') : null;

  return (
    <DashboardLayout>
      <ExplorerLayout explorer={explorer}>
      <SplitLayout side={side}>
      <Page data-testid="question-bank-page">
        <DataGrid
          title="Question bank"
          headerExtra={scopeLabel && (
            <button type="button" onClick={() => setTreeKey(null)} className="inline-flex items-center gap-1 h-8 px-2.5 rounded border border-shell-primary/40 bg-shell-primary/5 text-xs text-shell-primary hover:bg-shell-primary/10 max-w-xs" title="Clear explorer selection" data-testid="scope-chip">
              <span className="truncate">{scopeLabel}</span> ×
            </button>
          )}
          rows={questions}
          loading={loading}
          onRefresh={fetchQuestions}
          searchKeys={['tags', 'explanation']}
          columns={[
            {
              key: 'question_text', header: 'Question',
              accessor: (q) => stripHtml(q.question_text),
              render: (q) => <span className="font-medium inline-flex items-center gap-1.5" title={stripHtml(q.question_text)}>{(q.media_url || /<img/i.test(q.question_text || '')) && <Image className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" title="Has a figure" />}{truncate(stripHtml(q.question_text))}</span>,
            },
            {
              key: 'question_type', header: 'Type', width: 170, accessor: (q) => getTypeLabel(q.question_type),
              render: (q) => {
                const TypeIcon = getQuestionTypeIcon(q.question_type);
                return (
                  <span className="inline-flex items-center gap-1.5">
                    <TypeIcon className="w-4 h-4 text-muted-foreground shrink-0" weight="duotone" />
                    <StatusBadge value={getTypeLabel(q.question_type)} tone="neutral" />
                  </span>
                );
              },
            },
            { key: 'topic', header: 'Topic', width: 140, accessor: (q) => q.topic || '', render: (q) => q.topic || '—', className: 'text-muted-foreground' },
            {
              key: 'difficulty', header: 'Difficulty', width: 110, accessor: (q) => q.difficulty || '',
              render: (q) => (q.difficulty ? <StatusBadge value={q.difficulty} tone={DIFFICULTY_TONE[q.difficulty] || 'neutral'} /> : '—'),
            },
            { key: 'course', header: 'Course', width: 160, accessor: (q) => getCourseTitle(q.course_id), render: (q) => getCourseTitle(q.course_id) || '—', className: 'text-muted-foreground' },
            {
              key: 'category', header: 'Category', width: 140, accessor: (q) => getCategoryById(q.category_id)?.name || '',
              render: (q) => {
                const cat = getCategoryById(q.category_id);
                if (!cat) return '—';
                return (
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                    {cat.name}
                  </span>
                );
              },
            },
            { key: 'points', header: 'Points', width: 90, align: 'right', accessor: (q) => q.points ?? 0 },
            { key: 'is_active', header: 'Status', width: 100, accessor: (q) => q.is_active !== false, render: (q) => <StatusBadge value={q.is_active !== false} /> },
          ]}
          rowTone={(q) => (q.is_active === false ? 'muted' : null)}
          onRowDoubleClick={handleEditQuestion}
          advancedSearch={(
            <div className="space-y-3">
              <div className="form-grid">
                <div className="field">
                  <Label className="text-xs">Type</Label>
                  <Select value={filters.question_type || "all"} onValueChange={(v) => setFilters({ ...filters, question_type: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="filter-type">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {QUESTION_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="field">
                  <Label className="text-xs">Difficulty</Label>
                  <Select value={filters.difficulty || "all"} onValueChange={(v) => setFilters({ ...filters, difficulty: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="filter-difficulty">
                      <SelectValue placeholder="All Difficulties" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Difficulties</SelectItem>
                      {DIFFICULTIES.map(d => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="field">
                  <Label className="text-xs">Category</Label>
                  <Select value={filters.category_id || "all"} onValueChange={(v) => setFilters({ ...filters, category_id: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="filter-category">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                            {'\u00a0\u00a0\u00a0'.repeat(c.depth || 0)}{c.depth ? '└ ' : ''}{c.name} ({c.total_question_count ?? c.question_count})
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="field">
                  <Label className="text-xs">Course</Label>
                  <Select value={filters.course_id || "all"} onValueChange={(v) => setFilters({ ...filters, course_id: v === "all" ? "" : v })}>
                    <SelectTrigger data-testid="filter-course">
                      <SelectValue placeholder="All Courses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Courses</SelectItem>
                      {courses.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    onClick={() => setFilters({ question_type: '', difficulty: '', course_id: '', category_id: '' })}
                  >
                    <X className="w-3.5 h-3.5" /> Clear filters
                  </Button>
                </div>
              )}
            </div>
          )}
          activeFilterCount={activeFilterCount}
          primaryActions={(
            <>
              <Button size="sm" className="h-9" onClick={handleCreateQuestion} data-testid="create-question-btn">
                <Plus className="w-4 h-4" /> New question
              </Button>
              <Button size="sm" variant="outline" className="h-9" onClick={() => { setUploadTarget({ course_id: filters.course_id || '', category_id: filters.category_id || '' }); openPane(setShowBulkUploadModal); }} data-testid="bulk-upload-btn">
                <Upload className="w-4 h-4" /> Bulk upload
              </Button>
              <Button size="sm" variant="outline" className="h-9" onClick={() => { closePane(); categoryMgr.open(); }} data-testid="manage-categories-btn">
                <FolderOpen className="w-4 h-4" /> Manage categories
              </Button>
            </>
          )}
          selectionActions={[
            { key: 'preview', label: 'Preview', icon: Eye, requires: 'any', onClick: (rows) => openPreview(rows) },
            { key: 'edit', label: 'Edit', icon: Pencil, onClick: ([q]) => handleEditQuestion(q) },
            { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: ([q]) => handleDeleteQuestion(q.id) },
          ]}
          emptyMessage="No questions found — create your first question or upload in bulk"
        />
      </Page>
      </SplitLayout>
      </ExplorerLayout>
    </DashboardLayout>
  );
}
